import { useMemo, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@nexus/ui"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { buildGroupbyColumns, type ListColumnDef } from "../lib/drilldown-list-columns"
import { extractString } from "../lib/drilldown-row"

interface DrilldownListViewProps {
  rows: Array<Record<string, unknown>>
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  segment1: string
  segment2: string
  segment3: string
  /** Total rows loaded from the server (unfiltered). Used as the pagination recheck key so client-side search filtering doesn't trigger spurious fetches. */
  loadedCount: number
  /** Identity columns before the group-by columns (e.g. Creative+Brand for competitors, Creative-only for My Brand). */
  leadingColumns: ListColumnDef[]
  /** Mode-specific metric columns after the group-by columns. */
  trailingColumns: ListColumnDef[]
  emptyStateMessage?: string
  /** Row key extractor; defaults to `hash || ad_id || index`. */
  rowKey?: (row: Record<string, unknown>, index: number) => string
  /** Opens the creative detail drawer when clicking a row (Competitors list). */
  onRowDrawerClick?: (row: Record<string, unknown>, index: number) => void
}

function defaultRowKey(row: Record<string, unknown>, index: number): string {
  return extractString(row.hash) || extractString(row.ad_id) || String(index)
}

export function DrilldownListView({
  rows,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  segment1,
  segment2,
  segment3,
  loadedCount,
  leadingColumns,
  trailingColumns,
  emptyStateMessage,
  rowKey = defaultRowKey,
  onRowDrawerClick,
}: DrilldownListViewProps) {
  const { t } = useTranslation()
  const resolvedEmptyStateMessage = emptyStateMessage ?? t("drilldown.noCreativesMatch", "No creatives match the applied filters.")
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const resizeRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null)

  const columns = useMemo(
    () => [...leadingColumns, ...buildGroupbyColumns(segment1, segment2, segment3), ...trailingColumns],
    [leadingColumns, segment1, segment2, segment3, trailingColumns],
  )

  const [colWidths, setColWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(columns.map((c) => [c.key, c.defaultWidth])),
  )

  function startResize(e: React.MouseEvent, col: string) {
    e.preventDefault()
    resizeRef.current = {
      col,
      startX: e.clientX,
      startWidth: colWidths[col] ?? 100,
    }

    function onMove(ev: MouseEvent) {
      const r = resizeRef.current
      if (!r) return
      const newWidth = Math.max(60, r.startWidth + ev.clientX - r.startX)
      setColWidths((prev) => ({ ...prev, [r.col]: newWidth }))
    }

    function onUp() {
      resizeRef.current = null
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
    }

    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
  }

  useInfiniteScroll(
    sentinelRef,
    scrollContainerRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    { recheckKey: loadedCount },
  )

  const colCount = columns.length

  return (
    <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-auto" data-drilldown-scroll>
        <Table style={{ tableLayout: "fixed" }}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: colWidths[c.key] ?? c.defaultWidth }} />
            ))}
          </colgroup>

          <TableHeader className="sticky top-0 z-10">
            <TableRow>
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={`relative overflow-hidden${c.align === "right" ? " text-right" : ""}`}
                >
                  {c.header}
                  <ResizeHandle onMouseDown={(e) => startResize(e, c.key)} />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-8 text-center text-muted-foreground">
                  {resolvedEmptyStateMessage}
                </TableCell>
              </TableRow>
            )}

            {rows.map((row, i) => (
              <TableRow
                key={rowKey(row, i)}
                className={onRowDrawerClick ? "cursor-pointer" : undefined}
                onClick={onRowDrawerClick ? () => onRowDrawerClick(row, i) : undefined}
              >
                {columns.map((c) => (
                  <TableCell key={c.key} className={c.cellClassName ?? "overflow-hidden"}>
                    {c.renderCell(row, i)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

      {hasNextPage && (
        <div ref={sentinelRef} className="flex items-center justify-center py-6">
          {isFetchingNextPage && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>
      )}
    </div>
  )
}

function ResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <div
      className="absolute right-0 top-0 h-full w-0.5 cursor-col-resize bg-border/40 hover:bg-border active:bg-primary/40"
      onMouseDown={onMouseDown}
    />
  )
}
